import React, { Fragment, useEffect, useState } from "react";
import { Card, Col, Modal, Row } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import Select from "react-select";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { accountGroupList, ledgerCreate, ledgerDelete, ledgerList, ledgerView } from "../../../services/accounts";

const OB_TYPES = [
  { value: "Dr", label: "Debit (Dr)" },
  { value: "Cr", label: "Credit (Cr)" },
];

export default function LedgerMaster() {
  const [list, setList]           = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [show, setShow]           = useState(false);
  const [lid, setLid]             = useState(0);
  const [ledgerName, setLedgerName] = useState("");
  const [group, setGroup]         = useState("");
  const [obBalance, setObBalance] = useState("");
  const [obType, setObType]       = useState(OB_TYPES[0]);
  const [error, setError]         = useState("");

  useEffect(() => { loadList(); loadGroups(); }, []);

  function loadList() {
    const temp = new FormData();
    const academyId = localStorage.getItem("AAcademy") || localStorage.getItem("academy") || "";
    if (academyId) temp.append("academy_id", academyId);
    ledgerList(temp).then((res) => { if (res?.status) setList(res.data); });
  }

  function loadGroups() {
    const temp = new FormData();
    const academyId = localStorage.getItem("AAcademy") || localStorage.getItem("academy") || "";
    if (academyId) temp.append("academy_id", academyId);
    accountGroupList(temp).then((res) => {
      if (res?.status) {
        setGroupOptions(res.data.map((g) => ({ value: g.id, label: `${g.group_name} (${g.group_type})` })));
      }
    });
  }

  function handleAdd() {
    setLid(0); setLedgerName(""); setGroup(""); setObBalance(""); setObType(OB_TYPES[0]); setError(""); setShow(true);
  }

  function handleEdit(id) {
    setLid(id); setLedgerName(""); setGroup(""); setObBalance(""); setError(""); setShow(true);
    ledgerView(id).then((res) => {
      if (res?.status) {
        const d = res.data;
        setLedgerName(d.ledger_name);
        setObBalance(d.opening_balance);
        setObType(OB_TYPES.find((t) => t.value === d.ob_type) || OB_TYPES[0]);
        setGroup(groupOptions.find((g) => g.value === d.group_id) || "");
      }
    });
  }

  function handleClose() { setShow(false); }

  function handleDelete(id) {
    Swal.fire({
      text: "Are you sure you want to delete this ledger?",
      icon: "warning", showCancelButton: true,
      confirmButtonColor: "#ac0404", cancelButtonColor: "#6259ca",
      confirmButtonText: "Delete", allowOutsideClick: false,
    }).then((result) => {
      if (result.isConfirmed) {
        ledgerDelete(id).then((res) => {
          if (res?.status) { toast.success(res.message); loadList(); }
          else toast.error(res?.message || "Cannot delete — ledger has transactions");
        });
      }
    });
  }

  function handleSubmit() {
    setError("");
    if (!ledgerName.trim()) { setError("Please enter ledger name"); return; }
    if (!group)             { setError("Please select account group"); return; }

    const payload = new FormData();
    payload.append("id", lid);
    payload.append("ledger_name", ledgerName);
    payload.append("group_id", group.value);
    payload.append("opening_balance", obBalance || 0);
    payload.append("ob_type", obType?.value || "Dr");
    const academyId = localStorage.getItem("AAcademy") || localStorage.getItem("academy") || "";
    if (academyId) payload.append("academy_id", academyId);
    ledgerCreate(payload).then((res) => {
      if (res?.status) { toast.success(res.message); handleClose(); loadList(); }
      else setError(res?.message || "Something went wrong");
    });
  }

  const columnDefs = [
    { headerName: "Ledger Name", field: "ledger_name", sortable: true, filter: true, floatingFilter: true, resizable: true, cellStyle: { textAlign: "left" } },
    { headerName: "Group",       field: "group.group_name", sortable: true, filter: true, floatingFilter: true, resizable: true, cellStyle: { textAlign: "left" } },
    { headerName: "Type",        field: "group.group_type", width: 120, resizable: true },
    { headerName: "Opening Bal", field: "opening_balance",  width: 140, resizable: true },
    { headerName: "Dr/Cr",       field: "ob_type",          width: 90,  resizable: true },
    {
      headerName: "Actions", field: "id", width: 120, filter: false, resizable: true,
      cellRenderer: (params) => (
        <div className="text-center">
          <a onClick={() => handleEdit(params.value)} className="btn-sm btn-info me-2"><i className="fa fa-edit" /></a>
          <a onClick={() => handleDelete(params.value)} className="btn-sm btn-danger"><i className="fa fa-trash" /></a>
        </div>
      ),
    },
  ];

  return (
    <Fragment>
      <div className="page-header reported-header">
        <div>
          <h2 className="main-content-title tx-24 mg-b-5">Ledger Master</h2>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a>Accounts</a></li>
            <li className="breadcrumb-item active">Ledger Master</li>
          </ol>
        </div>
      </div>

      <Row className="row-sm mx-0 mt-3">
        <Col xxl={12} lg={12} md={12}>
          <Card className="custom-card overflow-hidden">
            <Card.Body>
              <div className="responsive">
                <div className="res-right">
                  <a onClick={handleAdd} className="btn my-1 me-2 btn-success tx-teko">Add Ledger</a>
                </div>
                <div className="ag-theme-alpine ag-style">
                  <AgGridReact columnDefs={columnDefs} rowData={list} pagination paginationPageSize={10} suppressDragLeaveHidesColumns />
                </div>
              </div>

              <Modal show={show} onHide={handleClose}>
                <Modal.Header>
                  <h4>{lid === 0 ? "Add" : "Edit"} Ledger</h4>
                </Modal.Header>
                <Modal.Body>
                  <Row className="row-sm mx-0">
                    <Col lg={12} md={12} className="mb-3">
                      <label className="mb-2">Ledger Name <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" placeholder="e.g. Cash A/c" value={ledgerName} onChange={(e) => setLedgerName(e.target.value)} />
                    </Col>
                    <Col lg={12} md={12} className="mb-3">
                      <label className="mb-2">Account Group <span className="text-danger">*</span></label>
                      <Select className="default-select" placeholder="Select Group" options={groupOptions} value={group} onChange={(e) => setGroup(e)} isClearable />
                    </Col>
                    <Col lg={8} md={8} className="mb-3">
                      <label className="mb-2">Opening Balance</label>
                      <input type="number" className="form-control" placeholder="0.00" value={obBalance} onChange={(e) => setObBalance(e.target.value)} />
                    </Col>
                    <Col lg={4} md={4} className="mb-3">
                      <label className="mb-2">Dr / Cr</label>
                      <Select className="default-select" options={OB_TYPES} value={obType} onChange={(e) => setObType(e)} />
                    </Col>
                    {error && <span className="text-danger">{error}</span>}
                  </Row>
                </Modal.Body>
                <Modal.Footer>
                  <a onClick={handleSubmit} className="btn btn-success">{lid === 0 ? "Submit" : "Update"}</a>
                  <a onClick={handleClose} className="btn btn-secondary">Close</a>
                </Modal.Footer>
              </Modal>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
}
